import type { MutableRefObject, RefObject } from "react";
import Lenis, { type VirtualScrollData } from "lenis";
import { resetCardPointerState } from "../lib/card-pointer-state";
import { cardTagScrollDelta } from "../lib/card-wheel";
import { gsap, useGSAP } from "../lib/gsap";

interface SmoothScrollProps {
  wrapperRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  routeCardTagGestures?: boolean;
  controllerRef?: MutableRefObject<SmoothScrollController | null>;
}

export interface SmoothScrollController {
  scrollToPosition: (top: number, immediate?: boolean) => void;
  scrollToTop: () => void;
}

export function SmoothScroll({
  wrapperRef,
  contentRef,
  routeCardTagGestures = false,
  controllerRef
}: SmoothScrollProps) {
  useGSAP(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    let lenis: Lenis | null = null;
    const scrollToPosition = (top: number, immediate = false) => {
      if (lenis) {
        lenis.scrollTo(top, { force: true, immediate });
        return;
      }
      wrapper.scrollTo({
        top,
        behavior:
          immediate ||
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth"
      });
    };
    const controller: SmoothScrollController = {
      scrollToPosition,
      scrollToTop: () => scrollToPosition(0)
    };
    if (controllerRef) controllerRef.current = controller;

    let activeCard: HTMLElement | null = null;
    const trackActiveCard = (event: PointerEvent) => {
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>(".model-card-shell")
          : null;
      const bounds = target?.getBoundingClientRect();
      const nextCard =
        target &&
        bounds &&
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom
          ? target
          : null;
      if (nextCard === activeCard) return;

      if (activeCard) resetCardPointerState(activeCard);
      activeCard = nextCard;
      if (activeCard) activeCard.dataset.wheelActive = "true";
    };
    const clearActiveCard = () => {
      if (activeCard) resetCardPointerState(activeCard);
      activeCard = null;
    };
    const routeCardWheel = (event: WheelEvent) => {
      const scrollDelta = cardTagScrollDelta(event);
      if (scrollDelta === null) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const directTagRow =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>(".card-tag-row")
          : null;
      const fallbackTagRow =
        activeCard?.querySelector<HTMLElement>(
          '.capability-row.card-tag-row[data-overflowing="true"]'
        ) ??
        activeCard?.querySelector<HTMLElement>(
          '.release-pills--scrollable.card-tag-row[data-overflowing="true"]'
        );
      const tagRow = directTagRow ?? fallbackTagRow;
      if (!tagRow || tagRow.dataset.overflowing !== "true") return;

      const deltaScale =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? tagRow.clientWidth
            : 1;
      tagRow.scrollLeft += scrollDelta * deltaScale;
    };

    if (routeCardTagGestures) {
      wrapper.addEventListener("wheel", routeCardWheel, {
        capture: true,
        passive: false
      });
      wrapper.addEventListener("pointermove", trackActiveCard, {
        capture: true,
        passive: true
      });
      wrapper.addEventListener("pointerleave", clearActiveCard, {
        passive: true
      });
    }

    const media = gsap.matchMedia();
    media.add("(prefers-reduced-motion: no-preference)", () => {
      const lenisRef: { current: Lenis | null } = { current: null };
      lenis = new Lenis({
        content,
        eventsTarget: wrapper,
        ...(routeCardTagGestures
          ? {
              lerp: 0.18,
              virtualScroll: ({ deltaY, event }: VirtualScrollData) => {
                if (!event.type.includes("wheel") || deltaY === 0) return true;

                const instance = lenisRef.current;
                if (!instance) return true;
                const pendingDirection = Math.sign(
                  instance.targetScroll - instance.animatedScroll
                );
                const inputDirection = Math.sign(deltaY);
                if (
                  pendingDirection !== 0 &&
                  inputDirection !== pendingDirection
                ) {
                  instance.targetScroll = instance.animatedScroll;
                }

                return true;
              }
            }
          : {}),
        wrapper
      });
      lenisRef.current = lenis;
      const update = (time: number) => lenis?.raf(time * 1000);

      gsap.ticker.add(update);

      return () => {
        gsap.ticker.remove(update);
        lenis?.destroy();
        lenis = null;
      };
    });

    return () => {
      wrapper.removeEventListener("wheel", routeCardWheel, {
        capture: true
      });
      wrapper.removeEventListener("pointermove", trackActiveCard, {
        capture: true
      });
      wrapper.removeEventListener("pointerleave", clearActiveCard);
      clearActiveCard();
      if (controllerRef?.current === controller) controllerRef.current = null;
      media.revert();
    };
  }, {
    dependencies: [controllerRef, routeCardTagGestures],
    revertOnUpdate: true
  });

  return null;
}
